import { IMicroAppListItemState } from '@locumsnest/core/src/lib/micro-app/interfaces';

import { AUTH_GROUPS } from '../../../auth-group/constants';
import { PRODUCT_CODES } from '../../constants';

type ProductCode = (typeof PRODUCT_CODES)[keyof typeof PRODUCT_CODES];
type AuthGroupMatchPolicy = 'any' | 'all';

interface MicroAppNavAccessRequirement {
  code: ProductCode;
  requiredAuthGroups: string[];
  matchPolicy: AuthGroupMatchPolicy;
}

const MICRO_APP_NAV_ACCESS_REQUIREMENTS: MicroAppNavAccessRequirement[] = [
  {
    code: PRODUCT_CODES.CONTROL,
    requiredAuthGroups: [
      AUTH_GROUPS.CONTROL_ADMIN_USER,
      AUTH_GROUPS.CONTROL_INTERMEDIATE,
      AUTH_GROUPS.CONTROL_READ_ONLY,
    ],
    matchPolicy: 'any',
  },
  {
    code: PRODUCT_CODES.CONTROL_ADMIN,
    requiredAuthGroups: [AUTH_GROUPS.LOCUMS_CONTROL_ADMIN, AUTH_GROUPS.CUSTOMER_ADMIN],
    matchPolicy: 'any',
  },
  {
    code: PRODUCT_CODES.MAINTENANCE,
    requiredAuthGroups: [AUTH_GROUPS.BANK_RATES_MANAGER],
    matchPolicy: 'any',
  },
  {
    code: PRODUCT_CODES.EXCEPTION_REPORTING,
    requiredAuthGroups: [
      AUTH_GROUPS.GUARDIAN_OF_SAFE_WORKING_HOURS,
      AUTH_GROUPS.DIRECTOR_OF_MEDICAL_EDUCATION,
      AUTH_GROUPS.HR_MEDICAL_STAFFING_ACCESS,
    ],
    matchPolicy: 'any',
  },
  {
    code: PRODUCT_CODES.PAYROLL_EXPORTS,
    requiredAuthGroups: [AUTH_GROUPS.PAYROLL_OFFICER],
    matchPolicy: 'any',
  },
];

export const filterMicroAppListForOfficerAuthGroups = (
  list: IMicroAppListItemState[],
  officerAuthGroups: string[],
) =>
  list.filter(
    ({ code }) =>
      code !== PRODUCT_CODES.AGENCY &&
      officerCanViewMicroApp(code as ProductCode, officerAuthGroups),
  );

const officerCanViewMicroApp = (code: ProductCode, officerAuthGroups: string[]) => {
  const accessRequirement = MICRO_APP_NAV_ACCESS_REQUIREMENTS.find(
    (requirement) => requirement.code === code,
  );

  if (!accessRequirement) {
    return true;
  }

  return officerMatchesAuthGroupRequirement(accessRequirement, officerAuthGroups);
};

const officerMatchesAuthGroupRequirement = (
  { requiredAuthGroups, matchPolicy }: MicroAppNavAccessRequirement,
  officerAuthGroups: string[],
) => {
  if (matchPolicy === 'all') {
    return requiredAuthGroups.every((authGroup) => officerAuthGroups.includes(authGroup));
  }

  return requiredAuthGroups.some((authGroup) => officerAuthGroups.includes(authGroup));
};
